import clsx from "clsx";
import { capitalize } from "lodash";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useDispatch, useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { selectGroups } from "@dashboard/groups/store/selectors";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import {
    closeBatchMode,
    removeBatchSelection,
    selectBatchActionEntity,
    selectBatchActionIsActiveDevice,
    selectBatchActionSelectionEntities,
    selectBatchActionTargetId,
    setBatchTargetId,
    updateGroup,
} from "@devices/store";
import { getNestedGroupsWithIndentation } from "@groups/utils";
import { Box, Typography } from "@mui/material";
import { PermissionService } from "@services/permissionService/permissionService";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";

import { PaneElement } from "../shared/PaneElement";
import { useStyle as useSharedStyle } from "../shared/style";

import PaneTable from "./PaneTable";

const useStyle = makeStyles()((theme) => ({
    wrapper: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
    },
    headerBox: {
        flexShrink: 0,
    },
    container: {
        padding: theme.spacing(3),
        width: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    // Hands its height to the PaneTable's shared TableLayout, which owns the scrollport —
    // same arrangement as a filling PaneTabPanel in the tabbed panels.
    tableWrapper: {
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    footer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: theme.spacing(3),
        flexShrink: 0,
    },
    tooltipIcon: {
        color: theme.palette.emteria.border.primary,
        marginRight: theme.spacing(2),
    },
    cancelButton: {
        marginRight: theme.spacing(1),
        marginLeft: theme.spacing(5),
    },
}));

export const MoveLicensePanel = () => {
    const { classes } = useStyle();
    const { classes: sharedClasses } = useSharedStyle();
    const dispatch = useDispatch();
    const batchActionEntity = useSelector(selectBatchActionEntity);
    const isBatchActionDevice = useSelector(selectBatchActionIsActiveDevice);
    const batchActionSelectionEntities = useSelector(selectBatchActionSelectionEntities, shallowEqual);
    const { data: workspace } = useCurrentWorkspace();
    const batchTargetId = useSelector(selectBatchActionTargetId);
    const selectionCount = batchActionSelectionEntities.length;

    const confirmAllowed = selectionCount > 0 && !!batchTargetId;
    const workspaceGroups = useSelector(selectGroups);

    const { groupAbility } = PermissionService();
    const { t } = useTranslation(["sidepanels", "common"]);

    const structuredGroups = getNestedGroupsWithIndentation(workspaceGroups);
    const options = structuredGroups.filter((group) =>
        groupAbility(workspaceGroups.find((g) => g.id === group.value) || null, workspace).can("update", "Group"),
    );

    const onChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (typeof selectedOption?.value === "string") {
            return;
        }

        dispatch(setBatchTargetId(selectedOption?.value ?? null));
    };
    const handleConfirmClose = () => {
        dispatch(closeBatchMode());
    };
    const handleConfirm = () => {
        if (batchTargetId) {
            dispatch(
                updateGroup({
                    id: batchTargetId,
                    addedLicenseIds: batchActionSelectionEntities.map((item) => item.id),
                    includeStatistics: true,
                    includeParents: true,
                }),
            )
                .unwrap()
                .then(() => {
                    showSuccessToast(t("sidepanels:moveLicensePanel.toast.movedLicenseSuccess", { count: selectionCount }));
                })
                .catch(({ message = t("sidepanels:moveLicensePanel.toast.movedLicenseFailed", { count: selectionCount }) }) =>
                    showErrorToast(message),
                );
        }
    };

    if (!batchActionEntity) {
        return null;
    }

    return (
        <Box className={classes.wrapper}>
            <PaneElement.HeaderBox className={classes.headerBox}>
                <Box className={sharedClasses.headerBox}>
                    <Typography variant="h6" className={sharedClasses.panelTitle}>
                        {t("sidepanels:moveLicensePanel.title")}
                    </Typography>
                    <Typography variant="body1" className={sharedClasses.panelSubtitle}>
                        {t("sidepanels:moveLicensePanel.subtitle.selectedCount", { count: selectionCount })}
                    </Typography>
                </Box>
                <SecondaryButton onClick={() => dispatch(closeBatchMode())}>
                    <i className="fa-solid fa-xmark" />
                </SecondaryButton>
            </PaneElement.HeaderBox>
            <Box className={classes.container}>
                <Box className={classes.tableWrapper}>
                    {!batchActionSelectionEntities.length ? (
                        <Typography align="left">{t("sidepanels:moveLicensePanel.selectItems")}</Typography>
                    ) : null}
                    {selectionCount ? (
                        <PaneTable
                            handleRemoveBatchItem={(id: number) => dispatch(removeBatchSelection(id))}
                            tableItems={batchActionSelectionEntities}
                            firstColumn={capitalize(`${batchActionEntity}s`)}
                        />
                    ) : null}
                </Box>
                <Box className={classes.footer}>
                    {isBatchActionDevice && (
                        <Tooltip
                            placement="top"
                            title={t("sidepanels:moveLicensePanel.tooltip.onlyGroupsOfEqualSubscriptionVariantAllowed")}
                        >
                            <i className={clsx("fa-regular fa-circle-question", classes.tooltipIcon)} />
                        </Tooltip>
                    )}

                    <SelectField
                        value={options.find((option) => option.value === batchTargetId)}
                        options={options}
                        disabled={!selectionCount}
                        onChange={onChange}
                        label={t("sidepanels:moveLicensePanel.input.selectGroup.caption")}
                        fullWidth
                        disableSorting
                    />

                    <SecondaryButton onClick={handleConfirmClose} className={classes.cancelButton}>
                        {t("common:button.cancel")}
                    </SecondaryButton>
                    <PrimaryButton onClick={handleConfirm} disabled={!confirmAllowed}>
                        {t("common:button.confirm")}
                    </PrimaryButton>
                </Box>
            </Box>
        </Box>
    );
};
